import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Progress } from "../../../components/ui/progress";
import { useAuth } from "../../../contexts/AuthContext";
import { useConvexProjects, useConvexProjectSales } from "../../../services/convexService";
import { toast } from "sonner";
import { Id } from "../../../../backend/_generated/dataModel";
import { useLanguage } from "../../../contexts/LanguageContext";
import CurrencyDisplay from "../../../components/currency/CurrencyDisplay";
import { api } from "../../../../backend/_generated/api";
import { useQuery } from "convex/react";
import {
  Home,
  LogOut,
  Eye,
  DollarSign,
  TrendingUp,
  Target,
  Users,
  Clock,
  FileText,
  Download,
  Image as ImageIcon,
  ArrowLeft,
  User,
  Mail,
  Phone
} from "lucide-react";

const ProjectSaleDetail = () => {
  const { projectSaleId } = useParams<{ projectSaleId: string }>();
  const navigate = useNavigate();
  const { user, logout, userType } = useAuth();
  const { t } = useLanguage();

  // First, treat the ID as a project ID to get the project details
  const projectData = useQuery(api.projects.getProjectById, projectSaleId ? { projectId: projectSaleId as Id<"projects"> } : "skip" as const);

  // Find the project sale associated with this project
  const projectSalesByProject = useQuery(api.projectSales.getProjectSalesByProject, projectSaleId ? { projectId: projectSaleId as Id<"projects"> } : "skip" as const);

  const projectSale = projectSalesByProject && projectSalesByProject.length > 0 ? projectSalesByProject[0] : null;

  const [loading, setLoading] = useState(true);

  // Set loading to false when project data is loaded
  useEffect(() => {
    // Loading is complete when project data is loaded
    if (projectData !== undefined) {
      setLoading(false);
    }
  }, [projectData]);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const handlePurchaseInquiry = () => {
    if (!user) {
      toast.error("You need to log in to submit a purchase inquiry");
      navigate("/login");
      return;
    }

    setShowPurchaseModal(true);
  };

  const handleGoBack = () => {
    if (userType === "admin" || user?.role === "admin") {
      navigate("/admin");
    } else {
      navigate("/project-sales");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Loading Project Details</h2>
          <p className="text-muted-foreground mb-6">Please wait while we load the project information. This may take a few seconds.</p>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!projectData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Project Not Found</h2>
          <p className="text-muted-foreground mb-6">The project you're looking for doesn't exist, has been removed, or you may not have permission to view it.</p>
          <div className="space-y-4">
            <Button asChild>
              <Link to="/project-sales">Back to Project Sales</Link>
            </Button>
            <p className="text-sm text-muted-foreground">If you believe this is an error, please contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!projectSale) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Project Sale Not Available</h2>
          <p className="text-muted-foreground mb-6">This project is marked for sale, but no specific sale listing is currently available.</p>
          <Button asChild>
            <Link to="/project-sales">Back to Project Sales</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Calculate funding percentage
  const fundingPercentage = projectData.fundingGoal > 0
    ? Math.round((projectData.currentFunding / projectData.fundingGoal) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <img src="/logo.png" alt="Knowvest.plus Logo" className="w-6 h-6 object-contain" />
              </div>
              <h1 className="font-bold text-xl">Knowvest.plus</h1>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleGoBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("common.backToHome")}
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/Home">
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Link>
              </Button>

              {user ? (
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              ) : (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/login">Login</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Project Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">{projectData.title}</h1>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary">{projectData.category}</Badge>
                <Badge variant="outline">For Sale</Badge>
                {projectData.status && (
                  <Badge variant={
                    projectData.status === "approved" ? "default" :
                      projectData.status === "pending_approval" ? "secondary" :
                        projectData.status === "funded" ? "default" :
                          projectData.status === "completed" ? "default" :
                            "outline"
                  }>
                    {projectData.status === "approved" && t("status.approved")}
                    {projectData.status === "pending_approval" && t("status.pending_approval")}
                    {projectData.status === "funded" && t("public.projects.status.funded")}
                    {projectData.status === "completed" && t("dashboard.projectholder.status.completed")}
                    {projectData.status === "draft" && t("dashboard.projectholder.status.draft")}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              {user?.id === projectData.ownerId && userType === "project-holder" && (
                <Button variant="default" size="lg" asChild>
                  <Link to={`/projects/${projectData._id}/edit`}>
                    <Eye className="w-4 h-4 mr-2" />
                    {t("common.edit")} {t("common.project")}
                  </Link>
                </Button>
              )}
              {(userType === "admin" || user?.role === "admin") && (
                <Button variant="outline" size="lg" asChild>
                  <Link to={`/projects/${projectData._id}/edit`}>
                    <Eye className="w-4 h-4 mr-2" />
                    {t("common.edit")} {t("common.project")}
                  </Link>
                </Button>
              )}
              <Button className="bg-hero-gradient hover:opacity-90 text-primary-foreground" onClick={handlePurchaseInquiry}>
                <DollarSign className="w-4 h-4 mr-2" />
                {t("projectSale.submitRequest") || "Submit Purchase Inquiry"}
              </Button>
            </div>
          </div>

          <p className="text-muted-foreground text-lg">{projectData.description}</p>
        </div>

        {/* Hero Section - Project Snapshot */}
        <div className="mb-8 p-6 bg-card rounded-xl border">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">{projectData.title}</h1>
              <p className="text-muted-foreground mb-4">{projectData.description.substring(0, 150)}{projectData.description.length > 150 ? '...' : ''}</p>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  <span className="font-medium">Asking Price: </span>
                  <span className="font-bold text-primary">{projectSale.saleAmount ? <CurrencyDisplay amount={projectSale.saleAmount} /> : 'TBD'}</span>
                </div>

                {projectData.status && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Status: </span>
                    <span>
                      {projectData.status === "approved" && t("status.approved")}
                      {projectData.status === "pending_approval" && t("status.pending_approval")}
                      {projectData.status === "funded" && t("public.projects.status.funded")}
                      {projectData.status === "completed" && t("dashboard.projectholder.status.completed")}
                      {projectData.status === "draft" && t("dashboard.projectholder.status.draft")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button className="bg-hero-gradient hover:opacity-90 text-primary-foreground" onClick={handlePurchaseInquiry}>
                <DollarSign className="w-4 h-4 mr-2" />
                {t("projectSale.submitRequest") || "Make an Offer"}
              </Button>
            </div>
          </div>
        </div>

        {/* Project Snapshot */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Project Snapshot</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Project Type</p>
                <p className="font-medium">{projectData.category}</p>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-medium">
                  {projectData.status === "approved" && t("status.approved")}
                  {projectData.status === "pending_approval" && t("status.pending_approval")}
                  {projectData.status === "funded" && t("public.projects.status.funded")}
                  {projectData.status === "completed" && t("dashboard.projectholder.status.completed")}
                  {projectData.status === "draft" && t("dashboard.projectholder.status.draft")}
                </p>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="font-medium">{projectData.location || 'Algeria'}</p>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Established</p>
                <p className="font-medium">{new Date(projectData.createdAt).getFullYear()}</p>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Ownership</p>
                <p className="font-medium">Full</p>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Verification</p>
                <p className="font-medium text-green-600">Verified by Platform</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mx-auto mb-2">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold"><CurrencyDisplay amount={projectData.fundingGoal || 0} /></p>
              <p className="text-sm text-muted-foreground">{t("common.goal")}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mx-auto mb-2">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold"><CurrencyDisplay amount={projectData.currentFunding || 0} /></p>
              <p className="text-sm text-muted-foreground">{t("common.raised")}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mx-auto mb-2">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold">{projectData.expectedROI || 0}%</p>
              <p className="text-sm text-muted-foreground">{t("common.expectedReturn")}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mx-auto mb-2">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold">{fundingPercentage}%</p>
              <p className="text-sm text-muted-foreground">{t("common.completed")}</p>
            </CardContent>
          </Card>
        </div>

        {/* Assets Included */}
        {(projectData as any).assetsIncluded && (projectData as any).assetsIncluded.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Assets Included</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Assets</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {(projectData as any).assetsIncluded.includes('designs') && (
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        Designs/Files
                      </li>
                    )}
                    {(projectData as any).assetsIncluded.includes('prototype') && (
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        Prototype
                      </li>
                    )}
                    {(projectData as any).assetsIncluded.includes('documentation') && (
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        Documentation
                      </li>
                    )}
                    {(projectData as any).assetsIncluded.includes('support') && (
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        Support Period
                      </li>
                    )}
                    {(projectData as any).assetsIncluded.includes('equipment') && (
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        Equipment
                      </li>
                    )}
                    {(projectData as any).assetsIncluded.includes('intellectualProperty') && (
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        Intellectual Property
                      </li>
                    )}
                    {(projectData as any).assetsIncluded.includes('other') && (
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        Other
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Financial Overview */}
        {projectData && (projectData as any).financialOverview && (projectData as any).financialOverview.trim() !== '' && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Financial Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="prose max-w-none">
                  <p className="text-muted-foreground whitespace-pre-line">
                    {(projectData as any).financialOverview}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Platform Verification Badge */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Platform Verification</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <div className="w-5 h-5 bg-green-500 rounded-full"></div>
              </div>
              <div>
                <h4 className="font-medium">Verified by Knowvest.plus</h4>
                <p className="text-sm text-muted-foreground">Ownership verified • Legal documents checked</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Documents */}
          <div className="lg:col-span-1 space-y-6">
            {/* Project Images */}
            {projectData.images && projectData.images.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    {t("public.projects.projectImages")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {projectData.images.map((image, index) => (
                      <div key={index} className="aspect-square bg-muted rounded-lg overflow-hidden">
                        <img
                          src={image}
                          alt={`Project image ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/placeholder-image.jpg'; // fallback image
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Project Documents */}
            {projectData.documents && projectData.documents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    {t("public.projects.projectDocuments")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {projectData.documents.map((document, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium truncate max-w-[160px]">Document {index + 1}</span>
                        </div>
                        <Button variant="outline" size="sm" asChild>
                          <a href={document} target="_blank" rel="noopener noreferrer">
                            <Download className="w-4 h-4 mr-1" />
                            {t("common.download")}
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Left Column - Images and Documents */}
          <div className="lg:col-span-1 space-y-6">
            {/* Project Images */}
            {projectData.images && projectData.images.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    {t("public.projects.projectImages")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {projectData.images.map((image, index) => (
                      <div key={index} className="aspect-square bg-muted rounded-lg overflow-hidden">
                        <img
                          src={image}
                          alt={`Project image ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/placeholder-image.jpg'; // fallback image
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Project Documents */}
            {projectData.documents && projectData.documents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    {t("public.projects.projectDocuments")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {projectData.documents.map((document, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium truncate max-w-[160px]">Document {index + 1}</span>
                        </div>
                        <Button variant="outline" size="sm" asChild>
                          <a href={document} target="_blank" rel="noopener noreferrer">
                            <Download className="w-4 h-4 mr-1" />
                            {t("common.download")}
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Project Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Description */}
            <Card>
              <CardHeader>
                <CardTitle>{t("public.projects.projectDescription")}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground whitespace-pre-line">
                  {projectData.description}
                </p>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <Card>
              <CardHeader>
                <CardTitle>Next Steps</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button className="flex-1 bg-hero-gradient hover:opacity-90 text-primary-foreground" onClick={handlePurchaseInquiry}>
                    <DollarSign className="w-4 h-4 mr-2" />
                    {t("projectSale.submitRequest") || "Make an Offer"}
                  </Button>
                  <Button variant="outline" className="flex-1">
                    <Phone className="w-4 h-4 mr-2" />
                    Book a Visit
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Category-specific sections */}
            {projectData.category === 'Innovation' && (
              <div className="space-y-6">
                {/* Innovation Overview */}
                <Card>
                  <CardHeader>
                    <CardTitle>Innovation Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground whitespace-pre-line mb-4">
                      {projectData.solutionDescription || "No solution description provided"}
                    </p>
                    {projectData.problemStatement && (
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">Problem Statement</h4>
                        <p className="text-muted-foreground whitespace-pre-line">{projectData.problemStatement}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Development Stage */}
                {projectData.developmentStage && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Development Stage</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{projectData.developmentStage}</Badge>
                        <p className="text-muted-foreground ml-2">
                          {projectData.developmentStage === 'Idea' && 'Early concept stage'}
                          {projectData.developmentStage === 'Prototype' && 'Prototype built and tested'}
                          {projectData.developmentStage === 'MVP' && 'Minimum viable product tested'}
                          {projectData.developmentStage === 'Patent Filed' && 'Patent application submitted'}
                          {projectData.developmentStage === 'Market-ready' && 'Ready for market'}
                          {projectData.developmentStage === 'Generating Revenue' && 'Already generating revenue'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Technology Details */}
                {projectData.technologyDetails && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Technology & Design Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground whitespace-pre-line">{projectData.technologyDetails}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Intellectual Property */}
                {projectData.ipStatus && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Intellectual Property & Ownership</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary">{projectData.ipStatus}</Badge>
                      </div>
                      <p className="text-muted-foreground">
                        This project has {projectData.ipStatus.toLowerCase()} protection.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Market Potential */}
                {projectData.marketPotential && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Market Potential</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground whitespace-pre-line">{projectData.marketPotential}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Monetization Options */}
                {projectData.monetizationOptions && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Monetization Options</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground whitespace-pre-line">{projectData.monetizationOptions}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Assets Included */}
                {(projectData as any).assetsIncluded && (projectData as any).assetsIncluded.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Assets Included in the Sale</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2">
                        {(projectData as any).assetsIncluded.includes('designs') && (
                          <span className="inline-block bg-muted px-3 py-1 rounded-full text-sm">Designs/Files</span>
                        )}
                        {(projectData as any).assetsIncluded.includes('prototype') && (
                          <span className="inline-block bg-muted px-3 py-1 rounded-full text-sm">Prototype</span>
                        )}
                        {(projectData as any).assetsIncluded.includes('documentation') && (
                          <span className="inline-block bg-muted px-3 py-1 rounded-full text-sm">Documentation</span>
                        )}
                        {(projectData as any).assetsIncluded.includes('support') && (
                          <span className="inline-block bg-muted px-3 py-1 rounded-full text-sm">Support Period</span>
                        )}
                        {(projectData as any).assetsIncluded.includes('equipment') && (
                          <span className="inline-block bg-muted px-3 py-1 rounded-full text-sm">Equipment</span>
                        )}
                        {(projectData as any).assetsIncluded.includes('intellectualProperty') && (
                          <span className="inline-block bg-muted px-3 py-1 rounded-full text-sm">Intellectual Property</span>
                        )}
                        {(projectData as any).assetsIncluded.includes('other') && (
                          <span className="inline-block bg-muted px-3 py-1 rounded-full text-sm">Other</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Purchase Inquiry Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="relative bg-background rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Purchase Inquiry</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPurchaseModal(false)}
                  className="absolute top-4 right-4"
                >
                  Close
                </Button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                // Handle form submission
                const formData = new FormData(e.currentTarget);
                const buyerName = formData.get('buyerName') as string;
                const buyerEmail = formData.get('buyerEmail') as string;
                const buyerPhone = formData.get('buyerPhone') as string;
                const budget = formData.get('budget') as string;
                const message = formData.get('message') as string;

                // Here you would typically submit the purchase inquiry to your backend
                // For now, we'll just show a success message
                console.log({
                  projectId: projectData._id,
                  projectTitle: projectData.title,
                  buyerName,
                  buyerEmail,
                  buyerPhone,
                  budget: budget || undefined,
                  message: message || undefined
                });

                toast.success("Your purchase inquiry has been submitted. The seller will contact you soon.");
                setShowPurchaseModal(false);
              }} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="buyerName">
                    Your Name <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="buyerName"
                      name="buyerName"
                      placeholder="Enter your full name"
                      required
                      className="pl-10"
                    />
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="buyerEmail">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="buyerEmail"
                      name="buyerEmail"
                      type="email"
                      placeholder="your.email@example.com"
                      required
                      className="pl-10"
                    />
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="buyerPhone">Phone Number</Label>
                  <div className="relative">
                    <Input
                      id="buyerPhone"
                      name="buyerPhone"
                      type="tel"
                      placeholder="+213 XXX XXX XXX"
                      className="pl-10"
                    />
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget">Budget</Label>
                  <div className="relative">
                    <Input
                      id="budget"
                      name="budget"
                      type="number"
                      placeholder="Enter your budget"
                      className="pl-10"
                    />
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell the seller about yourself and why you're interested in this project..."
                    rows={4}
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowPurchaseModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    Submit Inquiry
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSaleDetail;